import { type ReactNode } from 'react';
import { Link } from 'react-router';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/utils/cn';

export interface BreadcrumbSegment {
  label: ReactNode;
  href?: string;
}

interface PageBreadcrumbProps {
  segments: BreadcrumbSegment[];
  /** Action buttons rendered on the right (Edit, Save, Delete, etc.) */
  actions?: ReactNode;
  className?: string;
}

/**
 * Thin breadcrumb bar for the top of detail pages.
 * The last segment is always the current page (non-linked, foreground colour).
 * Earlier segments are clickable links.
 */
export function PageBreadcrumb({
  segments,
  actions,
  className,
}: PageBreadcrumbProps) {
  return (
    <div
      className={cn(
        'flex items-center justify-between min-h-[36px]',
        className
      )}
    >
      <nav className="flex items-center gap-1 text-sm min-w-0 overflow-hidden">
        {segments.map((segment, index) => {
          const isLast = index === segments.length - 1;
          return (
            <span
              key={
                segment.href ??
                (typeof segment.label === 'string' ? segment.label : 'current')
              }
              className="flex items-center gap-1 min-w-0"
            >
              {index > 0 && (
                <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground/60" />
              )}
              {isLast || !segment.href ? (
                <span
                  className={cn(
                    'truncate',
                    isLast
                      ? 'text-foreground font-medium'
                      : 'text-muted-foreground'
                  )}
                >
                  {segment.label}
                </span>
              ) : (
                <Link
                  to={segment.href}
                  className="text-muted-foreground hover:text-foreground transition-colors truncate"
                >
                  {segment.label}
                </Link>
              )}
            </span>
          );
        })}
      </nav>

      {actions && (
        <div className="flex items-center gap-2 flex-shrink-0 ml-4">
          {actions}
        </div>
      )}
    </div>
  );
}
